import { useContext, createContext, useRef, useState, useEffect } from 'react';

import type { TCardName } from 'components/direct-sales/select-options/config/cards-config';

type ScreenContextType = {
  caveatRef: HTMLElement | null;
  setCaveatRef: (ref: HTMLElement | null) => void;
  caveatItemIdsRef: React.RefObject<string[]>;
  registerCard: (card: TCardName) => void;
  registerCta: (cta: string) => void;
  cards: TCardName[];
  ctasRef: React.RefObject<string[]>;
};
/* istanbul ignore next -- @preserve */
export const defaultScreenContext: ScreenContextType = {
  caveatRef: null,
  setCaveatRef: () => {},
  caveatItemIdsRef: { current: [] },
  registerCard: () => undefined,
  registerCta: () => undefined,
  cards: [],
  ctasRef: { current: [] }
};
export const ScreenContext = createContext(defaultScreenContext);

export const useCaveatRegistry = () => {
  const [caveatRef, setCaveatRef] = useState<HTMLElement | null>(null);
  const caveatItemIdsRef = useRef([]);

  useEffect(() => {
    return () => {
      // cleanup when screen is unmounted
      caveatItemIdsRef.current = [];
      setCaveatRef(null);
    };
  }, []);

  return {
    caveatRef,
    setCaveatRef,
    caveatItemIdsRef
  };
};

export const useCardsRegistry = () => {
  const [cards, setCards] = useState<TCardName[]>([]);
  const ctasRef = useRef<string[]>([]);

  const registerCard = (card: TCardName) =>
    setCards((prev) => Array.from(new Set([...prev, card])));

  const registerCta = (cta: string) => {
    ctasRef.current = Array.from(new Set([...ctasRef.current, cta]));
  };

  useEffect(() => {
    return () => {
      // cleanup when screen is unmounted
      setCards([]);
      ctasRef.current = [];
    };
  }, []);

  return { registerCard, registerCta, cards, ctasRef };
};

export const useScreen = () => {
  return useContext(ScreenContext);
};
